// SPDX-License-Identifier: Elastic-2.0
/**
 * Boot order matters. Read top-to-bottom:
 *
 *  1. theme/index.css → registers design-system tokens (T-002).
 *  2. i18n side-effect imports → i18next.init() + useLocale store +
 *     <html lang> sync (T-003).
 *  3. Fixture mode (dev-only) → seeds localStorage + starts the MSW
 *     browser worker. No-op outside `npm run dev:fixture`.
 *  4. API client initializers → wire VITE_*_BASE_URL + token resolvers
 *     into the generated clients BEFORE any component issues a request
 *     (T-005 gateway, T-006 accounts).
 *  5. <App /> mounts; React Router takes over rendering (T-004).
 *
 * Later tasks layer on:
 *  - T-007: auth store init + refresh orchestrator before render.
 *  - T-008: WebSocket connect after auth init.
 *  - T-011: ErrorBoundary wrap.
 */
import "./theme/index.css";

// i18n side effects: i18next.init() registers EN bundle eagerly; useLocale
// store self-instantiates and applies <html lang>.
import "./i18n";
import { initialLocaleReady } from "./i18n/useLocale";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { initGatewayClient } from "./api/gateway-client";
import { initAccountsClient } from "./api/accounts-client";
import { initAuth } from "./api/auth-store-init";
import { initWebSocket } from "./api/useWebSocket";
import { App } from "./App";
import { enableFixtureMode, FIXTURE_MODE_ENABLED } from "./fixtures";

/**
 * Async bootstrap — wraps the sync init chain so fixture-mode (which
 * needs to register a service worker BEFORE the first fetch fires)
 * can await its setup. In production the await resolves immediately
 * (`enableFixtureMode` returns on the !FIXTURE_MODE_ENABLED branch).
 */
async function boot(): Promise<void> {
  await initialLocaleReady;

  // Fixture mode goes BEFORE the API clients init: the MSW worker
  // must be registered before initGatewayClient / initAccountsClient
  // make their first network call from the auth refresh orchestrator.
  await enableFixtureMode();

  // Configure generated API clients with base URLs + auth token resolvers.
  // Both calls are idempotent.
  initGatewayClient();
  initAccountsClient();
  // Hydrate the auth store from localStorage + start the refresh orchestrator.
  // MUST run AFTER the client inits (orchestrator may immediately fire a refresh
  // against the accounts base URL) and BEFORE render (so AuthGuard observes the
  // resolved mode on first paint, avoiding a re-auth flash).
  initAuth();
  // Open the WebSocket to F-300 if a JWT is present (no-op for unauthenticated
  // boots). Subscribes to auth-store changes so post-login session triggers an
  // automatic connect; close-code 1008 / auth_error emit reauth via eventBus.
  initWebSocket();

  const container = document.getElementById("root");
  if (!container) {
    throw new Error("Root container #root not found in index.html");
  }

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

// Surface boot errors loudly in dev — production catches them via the
// ErrorBoundary inside <App />, but a pre-render failure (fixture
// worker failed to register, etc.) deserves an explicit log so the
// dev tester knows their setup is broken rather than seeing a blank.
void boot().catch((err: unknown) => {
  // eslint-disable-next-line no-console
  console.error(
    `[boot] failed${FIXTURE_MODE_ENABLED ? " (fixture mode active)" : ""}:`,
    err,
  );
});
